import React from 'react';
import { useTranslation } from 'react-i18next';
import { DiscoveryCard } from '../../../types/lesson';
import { useLessonPersistedState } from '../useLessonPersistedState';
import TranslateToggle from './TranslateToggle';

interface Props {
    lessonId?: number;
    items: DiscoveryCard[];
    teacherMode: boolean;
    onAllRevealed?: () => void;
    onWordAnswered?: (word: string, correct: boolean, hintUsed?: boolean) => void;
    sourceLanguage?: string;
    targetLanguage?: string;
}

// The LLM sometimes writes an example as a template sentence with its own blank (e.g. "Overtime
// work can __________ an employee's exhaustion.") instead of the word already filled in — since
// every example under one discovery card demonstrates the same correct answer, any run of 3+
// underscores can be safely replaced with it. A plain (already-complete) example has no such run
// and passes through unchanged.
function renderExampleWithAnswer(example: string, answer: string): React.ReactNode {
    const parts = example.split(/_{3,}/);
    if (parts.length === 1) return example;
    return parts.map((part, i) => (
        <React.Fragment key={i}>
            {part}
            {i < parts.length - 1 && <span className="filled-answer">{answer}</span>}
        </React.Fragment>
    ));
}

// Splits on any run of 3+ underscores rather than an exact "___" literal, and collapses
// everything after the first blank into one trailing segment — see the identical fix/comment in
// WordFormationSection.tsx (a real generation bug there duplicated the blank marker elsewhere in
// the sentence, which an exact 2-way split would either miss or misplace).
function splitOnBlank(sentence: string): [string, string] {
    const raw = sentence.split(/_{3,}/);
    return [raw[0], raw.slice(1).join('')];
}

const DiscoveryCardsSection: React.FC<Props> = ({ lessonId, items, teacherMode, onAllRevealed, onWordAnswered, sourceLanguage, targetLanguage }) => {
    const { t } = useTranslation('lesson');
    const [selected, setSelected] = useLessonPersistedState<Record<number, number>>(lessonId, 'discovery.selected', {});
    // Independent of teacherMode/answer-reveal — a translation is a convenience aid, not an
    // answer, so it always requires its own explicit click (same precedent as
    // SpeakingChallengeSection's "Show useful language" toggle).
    const [showTranslation, setShowTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'discovery.showTranslation', {});
    const [showExampleTranslation, setShowExampleTranslation] = useLessonPersistedState<Record<string, boolean>>(lessonId, 'discovery.showExampleTranslation', {});
    const [explanationFallback, setExplanationFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'discovery.explanationFallback', {});
    const [exampleFallback, setExampleFallback] = useLessonPersistedState<Record<string, string>>(lessonId, 'discovery.exampleFallback', {});
    // Pre-answer icons — sentence_with_blank and the option list — can hint at the answer, so
    // opening one before a card is answered flags it for a reduced score at Finish (see
    // LessonPlayer.handleFinish). The post-reveal explanation/example icons above are never
    // clickable before answering (they're not even rendered yet), so they need no such tracking.
    const [showSentenceTranslation, setShowSentenceTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'discovery.showSentenceTranslation', {});
    const [sentenceFallback, setSentenceFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'discovery.sentenceFallback', {});
    const [showOptionsTranslation, setShowOptionsTranslation] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'discovery.showOptionsTranslation', {});
    const [optionsFallback, setOptionsFallback] = useLessonPersistedState<Record<number, string>>(lessonId, 'discovery.optionsFallback', {});
    const [hintUsed, setHintUsed] = useLessonPersistedState<Record<number, boolean>>(lessonId, 'discovery.hintUsed', {});

    const markHint = (cardIdx: number) => {
        if (selected[cardIdx] !== undefined) return;
        setHintUsed((prev) => (prev[cardIdx] ? prev : { ...prev, [cardIdx]: true }));
    };

    const choose = (cardIdx: number, optIdx: number) => {
        if (selected[cardIdx] !== undefined) return;
        const next = { ...selected, [cardIdx]: optIdx };
        setSelected(next);
        const card = items[cardIdx];
        onWordAnswered?.(card.options[card.correct_option_index], optIdx === card.correct_option_index, hintUsed[cardIdx]);
        if (Object.keys(next).length === items.length) onAllRevealed?.();
    };

    return (
        <div className="lesson-panel">
            <div>
                <div className="lesson-panel-kicker">{t('sections.discover.kicker', 'Notice the pattern')}</div>
                <h2>{t('sections.discover.title', 'Discover the Pattern')}</h2>
                <p className="lesson-panel-desc">
                    {t('sections.discover.desc', 'Choose an answer first — the rule is revealed only after you commit.')}
                </p>
            </div>
            <div className="lesson-qgrid">
                {items.map((card, i) => {
                    const chosen = selected[i];
                    const revealed = chosen !== undefined;
                    const parts = splitOnBlank(card.sentence_with_blank);
                    return (
                        <div className="lesson-qcard" key={i}>
                            <div className="lesson-qnum">{t('sections.card_n_of', 'Card {{n}} of {{total}}', { n: i + 1, total: items.length })}</div>
                            <div className="lesson-blank-sentence lesson-word-target">
                                {parts[0]}
                                <span className={`lesson-gap${(revealed || teacherMode) ? ' filled' : ''}`}>
                                    {(revealed || teacherMode) ? card.options[card.correct_option_index] : '___'}
                                </span>
                                {parts[1]}
                                <TranslateToggle
                                    text={card.sentence_with_blank}
                                    fallback={sentenceFallback[i]}
                                    onFallbackFetched={(text) => setSentenceFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showSentenceTranslation[i]}
                                    onToggle={() => { markHint(i); setShowSentenceTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            <div className="lesson-opt-row">
                                {card.options.map((opt, oi) => {
                                    let cls = 'lesson-opt';
                                    if (revealed) {
                                        if (oi === card.correct_option_index) cls += ' correct';
                                        else if (oi === chosen) cls += ' incorrect';
                                    }
                                    return (
                                        <button key={oi} className={cls} disabled={revealed} onClick={() => choose(i, oi)}>
                                            {opt}
                                        </button>
                                    );
                                })}
                                <TranslateToggle
                                    text={card.options.join(' / ')}
                                    fallback={optionsFallback[i]}
                                    onFallbackFetched={(text) => setOptionsFallback((prev) => ({ ...prev, [i]: text }))}
                                    sourceLanguage={sourceLanguage}
                                    targetLanguage={targetLanguage}
                                    shown={!!showOptionsTranslation[i]}
                                    onToggle={() => { markHint(i); setShowOptionsTranslation((prev) => ({ ...prev, [i]: !prev[i] })); }}
                                />
                            </div>
                            {(revealed || teacherMode) && (
                                <div className="lesson-reveal lesson-word-target">
                                    <div className="lesson-reveal-explanation">
                                        <strong>{card.pattern_explanation}</strong>
                                        <TranslateToggle
                                            text={card.pattern_explanation}
                                            native={card.pattern_explanation_native}
                                            fallback={explanationFallback[i]}
                                            onFallbackFetched={(text) => setExplanationFallback((prev) => ({ ...prev, [i]: text }))}
                                            sourceLanguage={sourceLanguage}
                                            targetLanguage={targetLanguage}
                                            shown={!!showTranslation[i]}
                                            onToggle={() => setShowTranslation((prev) => ({ ...prev, [i]: !prev[i] }))}
                                        />
                                    </div>
                                    <ul>
                                        {card.examples.map((ex, ei) => {
                                            const exKey = `${i}-${ei}`;
                                            return (
                                                <li key={ei}>
                                                    {renderExampleWithAnswer(ex, card.options[card.correct_option_index])}
                                                    <TranslateToggle
                                                        text={ex}
                                                        native={card.examples_native?.[ei]}
                                                        fallback={exampleFallback[exKey]}
                                                        onFallbackFetched={(text) => setExampleFallback((prev) => ({ ...prev, [exKey]: text }))}
                                                        sourceLanguage={sourceLanguage}
                                                        targetLanguage={targetLanguage}
                                                        shown={!!showExampleTranslation[exKey]}
                                                        onToggle={() => setShowExampleTranslation((prev) => ({ ...prev, [exKey]: !prev[exKey] }))}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DiscoveryCardsSection;
